<script setup lang="ts">
import { computed, ref } from 'vue'
import { formatTime } from '@/utils/datetime'
import { ArrowUpDown, ChevronDown, Edit, Building, Folder, X, BadgeCheck } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { MemberAvatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toast'
import UsersEditDialog from './UsersEditDialog.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { API, HOST } from '@/utils/http'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import type { AdminUser, VerifyHistoryEntry } from '@/types/admin'

const props = defineProps<{
  users: AdminUser[]
  loading: boolean
}>()

const emit = defineEmits<{
  (e: 'user-updated'): void
}>()

const store = useStore()
const router = useRouter()
const settings = computed(() => store.getters['user/getUserSettings'])
const expandedRows = ref(new Set<number>())
const editingUser = ref<AdminUser | null>(null)

// Confirm dialog refs
const confirmOrgRemovalDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const confirmProjectRemovalDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingOrgRemoval = ref<{ userId: number; organizationId: number; orgName: string; isDirectMember: boolean } | null>(null)
const pendingProjectRemoval = ref<{ userId: number; projectMemberId: number; projectName: string } | null>(null)

const toggleRowExpansion = (userId: number) => {
  if (expandedRows.value.has(userId)) {
    expandedRows.value.delete(userId)
  } else {
    expandedRows.value.add(userId)
    fetchVerifyHistory(userId)
  }
  expandedRows.value = new Set(expandedRows.value) // Force reactivity
}

const openEditDialog = (user: AdminUser) => {
  editingUser.value = user
}

const promptRemoveFromOrganization = (userId: number, organizationId: number, orgName: string, isDirectMember: boolean) => {
  pendingOrgRemoval.value = { userId, organizationId, orgName, isDirectMember }
  confirmOrgRemovalDialog.value?.open()
}

const promptRemoveFromProject = (userId: number, projectMemberId: number, projectName: string) => {
  pendingProjectRemoval.value = { userId, projectMemberId, projectName }
  confirmProjectRemovalDialog.value?.open()
}

const removeFromOrganization = async () => {
  if (!pendingOrgRemoval.value) return

  const { userId, organizationId, orgName, isDirectMember } = pendingOrgRemoval.value

  try {
    const response = await API({
      method: 'DELETE',
      url: `${HOST}/admin/users/${userId}/organizations/${organizationId}`
    })

    const projectsRemoved = response.data?.projects_removed || 0
    const removedFromOrg = response.data?.removed_from_organization || false

    let message = ''
    if (removedFromOrg && projectsRemoved > 0) {
      message = `User removed from ${orgName} and ${projectsRemoved} project(s)`
    } else if (removedFromOrg) {
      message = `User removed from ${orgName}`
    } else if (projectsRemoved > 0) {
      message = `User removed from ${projectsRemoved} project(s) in ${orgName}`
    }

    toast({
      title: message
    })

    emit('user-updated')
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to remove user'
    toast({
      title: errorMessage,
      variant: 'destructive'
    })
  } finally {
    pendingOrgRemoval.value = null
  }
}

const removeFromProject = async () => {
  if (!pendingProjectRemoval.value) return

  const { userId, projectMemberId, projectName } = pendingProjectRemoval.value

  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/admin/users/${userId}/projects/${projectMemberId}`
    })

    toast({
      title: `User removed from ${projectName}`
    })

    emit('user-updated')
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to remove user from project'
    toast({
      title: errorMessage,
      variant: 'destructive'
    })
  } finally {
    pendingProjectRemoval.value = null
  }
}

const verifyHistory = ref<Record<number, VerifyHistoryEntry[]>>({})
const verifyHistoryLoading = ref(new Set<number>())
const verifyLoading = ref(new Set<number>())

const toggleVerification = async (user: AdminUser, checked: boolean) => {
  verifyLoading.value.add(user.member_id)
  try {
    await API({
      method: 'POST',
      url: `${HOST}/admin/users/${user.member_id}/verify?verified=${checked}`
    })
    toast({ title: checked ? 'User verified' : 'User unverified' })
    // Clear cached history so it refetches
    delete verifyHistory.value[user.member_id]
    fetchVerifyHistory(user.member_id)
    emit('user-updated')
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to update verification'
    toast({ title: errorMessage, variant: 'destructive' })
  } finally {
    verifyLoading.value.delete(user.member_id)
  }
}

const fetchVerifyHistory = async (userId: number) => {
  if (verifyHistory.value[userId] || verifyHistoryLoading.value.has(userId)) return
  verifyHistoryLoading.value.add(userId)
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/users/${userId}/verify-history`
    })
    verifyHistory.value[userId] = response.data.history || []
  } catch {
    verifyHistory.value[userId] = []
  } finally {
    verifyHistoryLoading.value.delete(userId)
  }
}

const getOrgRemovalTooltip = (organizationMemberId: number | null | undefined): string => {
  return organizationMemberId
    ? 'Remove from organization and projects'
    : 'Remove from projects only'
}
</script>

<template>
  <div class="h-full">
    <div v-if="loading" class="p-4">
      <div class="space-y-3">
        <div v-for="i in 5" :key="i" class="flex items-center space-x-3 animate-pulse">
          <div class="h-10 w-10 bg-muted rounded-full"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-muted rounded w-1/4"></div>
            <div class="h-4 bg-muted rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom table implementation with expandable rows -->
    <div v-else class="border rounded-md">
      <table class="w-full">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              <Button variant="ghost" size="sm" @click="() => {}">
                Name <ArrowUpDown class="ml-1 h-3 w-3 inline-block" />
              </Button>
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              <Button variant="ghost" size="sm" @click="() => {}">
                Contact <ArrowUpDown class="ml-1 h-3 w-3 inline-block" />
              </Button>
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Orgs
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Projects
            </th>
            <th class="h-12 text-left align-middle font-medium text-muted-foreground">
              <Button variant="ghost" size="sm" @click="() => {}">
                Member since
              </Button>
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="user in users" :key="user.member_id">
            <!-- Main row -->
            <tr class="border-b hover:bg-muted/50">
              <td class="p-4 align-middle">
                <div class="flex items-center gap-3">
                  <MemberAvatar
                    :avatar_url="user.avatar_url"
                    :display_name="user.display_name || user.first_name || 'Unknown'"
                    :verified="user.verified"
                  />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium">{{ user.first_name }} {{ user.last_name }}</span>
                      <Badge v-if="user.is_super_user" variant="secondary" class="text-xs">Admin</Badge>
                    </div>
                  </div>
                </div>
              </td>
              <td class="p-4 align-middle">
                <div class="text-muted-foreground">
                  <div v-if="user.email" class="lowercase text-sm">{{ user.email }}</div>
                </div>
              </td>
              <td class="p-4 align-middle">
                <div class="text-sm text-muted-foreground">{{ user.organization_count || 0 }}</div>
              </td>
              <td class="p-4 align-middle">
                <div class="text-sm text-muted-foreground">{{ user.project_count || 0 }}</div>
              </td>
              <td class="p-4 align-middle">
                <div class="text-muted-foreground text-sm whitespace-nowrap">
                  {{ formatTime(user.created_at, settings?.tz || 'UTC', 'D MMM YYYY') }}
                </div>
              </td>
              <td class="p-4 align-middle">
                <div class="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8"
                    @click="openEditDialog(user)"
                  >
                    <Edit class="h-4 w-4" />
                  </Button>
                  <Button
                    v-if="user.organization_count > 0 || user.project_count > 0"
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8"
                    @click="toggleRowExpansion(user.member_id)"
                  >
                    <ChevronDown
                      class="h-4 w-4 transition-transform duration-200"
                      :class="{ 'rotate-180': expandedRows.has(user.member_id) }"
                    />
                  </Button>
                </div>
              </td>
            </tr>

            <!-- Expanded row -->
            <tr v-if="expandedRows.has(user.member_id)" class="border-b bg-muted/50">
              <td colspan="6" class="p-0">
                <div class="px-8 py-4">
                  <div class="grid grid-cols-2 gap-8">
                    <!-- Organizations section -->
                    <div v-if="user.organizations && user.organizations.length > 0">
                      <div class="flex items-center gap-2 mb-3 text-sm font-medium text-foreground">
                        <Building class="h-4 w-4" />
                        <span>Organizations ({{ user.organizations.length }})</span>
                      </div>
                      <div class="max-h-[400px] overflow-y-auto pr-2">
                        <ul class="space-y-1">
                          <li
                            v-for="org in user.organizations"
                            :key="org.organization_id"
                            class="flex items-center justify-between py-1"
                          >
                            <div class="flex items-center gap-2 flex-1 min-w-0">
                              <a
                                :href="`/organization/${org.organization_id}`"
                                class="text-sm text-primary hover:underline cursor-pointer truncate"
                                @click.prevent="router.push(`/organization/${org.organization_id}`)"
                              >
                                {{ org.organization_name || `Organization ${org.organization_id}` }}
                              </a>
                              <Badge v-if="!org.organization_member_id" variant="secondary" class="text-xs flex-shrink-0">
                                via projects
                              </Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              class="h-7 w-7 flex-shrink-0"
                              @click="promptRemoveFromOrganization(user.member_id, org.organization_id, org.organization_name, !!org.organization_member_id)"
                              :title="getOrgRemovalTooltip(org.organization_member_id)"
                            >
                              <X class="h-4 w-4 text-destructive" />
                            </Button>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <!-- Verified toggle -->
                    <div class="col-span-2">
                      <div class="flex items-center justify-between rounded-lg border p-3">
                        <div class="space-y-0.5">
                          <Label>Verified Member</Label>
                          <p class="text-xs text-muted-foreground">Verified members cannot change their name, email, or phone</p>
                        </div>
                        <Switch
                          :model-value="user.verified"
                          :disabled="verifyLoading.has(user.member_id)"
                          @update:model-value="(checked: boolean) => toggleVerification(user, checked)"
                        />
                      </div>
                    </div>

                    <!-- Verification History section -->
                    <div v-if="verifyHistory[user.member_id]?.length > 0" class="col-span-2 mb-4">
                      <div class="flex items-center gap-2 mb-3 text-sm font-medium text-foreground">
                        <BadgeCheck class="h-4 w-4" />
                        <span>Verification History</span>
                      </div>
                      <div class="max-h-[200px] overflow-y-auto pr-2">
                        <ul class="space-y-1">
                          <li
                            v-for="entry in verifyHistory[user.member_id]"
                            :key="entry.id"
                            class="flex items-center gap-3 py-1 text-sm"
                          >
                            <Badge :variant="entry.verified ? 'default' : 'outline'" class="text-xs">
                              {{ entry.verified ? 'Verified' : 'Unverified' }}
                            </Badge>
                            <span class="text-muted-foreground">
                              by {{ entry.changed_by_display_name || 'Unknown' }}
                            </span>
                            <span class="text-xs text-muted-foreground">
                              {{ formatTime(entry.created_at, settings?.tz || 'UTC', 'D MMM YYYY HH:mm') }}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <!-- Projects section -->
                    <div v-if="user.projects && user.projects.length > 0">
                      <div class="flex items-center gap-2 mb-3 text-sm font-medium text-foreground">
                        <Folder class="h-4 w-4" />
                        <span>Projects ({{ user.projects.length }})</span>
                      </div>
                      <div class="max-h-[400px] overflow-y-auto pr-2">
                        <ul class="space-y-1">
                          <li
                            v-for="project in user.projects"
                            :key="project.project_id"
                            class="py-1"
                          >
                            <div class="flex items-center justify-between">
                              <div class="flex-1 min-w-0">
                                <a
                                  :href="`/project/${project.project_id}`"
                                  class="text-sm text-primary hover:underline cursor-pointer block truncate"
                                  @click.prevent="router.push(`/project/${project.project_id}`)"
                                >
                                  {{ project.project_name || `Project ${project.project_id}` }}
                                </a>
                                <div v-if="project.organization_name" class="text-xs text-muted-foreground truncate">
                                  {{ project.organization_name }}
                                </div>
                              </div>
                              <div class="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  v-if="project.project_member_id"
                                  variant="ghost"
                                  size="icon"
                                  class="h-7 w-7"
                                  @click="promptRemoveFromProject(user.member_id, project.project_member_id, project.project_name)"
                                  title="Remove from project"
                                >
                                  <X class="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Edit dialog -->
    <UsersEditDialog
      v-if="editingUser"
      :user="editingUser"
      :open="!!editingUser"
      @update:open="(val) => { if (!val) editingUser = null }"
      @user-updated="() => { editingUser = null; emit('user-updated') }"
    />

    <!-- Confirm dialogs -->
    <DialogConfirm
      ref="confirmOrgRemovalDialog"
      title="Remove user from organization?"
      :description="pendingOrgRemoval?.isDirectMember
        ? 'This will remove the user from the organization and all projects within it. This action cannot be undone.'
        : 'This user is only a member via projects. This will remove them from all projects in this organization. This action cannot be undone.'"
      @confirm="removeFromOrganization"
    />

    <DialogConfirm
      ref="confirmProjectRemovalDialog"
      title="Remove user from project?"
      description="This will remove the user from this project. This action cannot be undone."
      @confirm="removeFromProject"
    />
  </div>
</template>
